class FilterDrawer extends HTMLElement {
  constructor() {
    super()

    this.panel = this.querySelector('#FilterDrawer-Panel')
    this.grid = document.querySelector('[data-collection-grid]')
    this.emptyEl = document.querySelector('[data-collection-empty-filter]')
    this.applyBtn = this.querySelector('[data-filter-apply]')
    this.countLabels = this.applyBtn ? this.applyBtn.querySelectorAll('.btn-cta-label-track > span') : []
    this.showProductsText = this.countLabels.length ? this.countLabels[0].textContent.trim() : 'Show products'

    this.items = this.grid ? this.collectItems() : []

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close())
    this.addEventListener('click', (evt) => {
      if (evt.target === this) this.close()
    })

    this.bindTriggers()
    this.bindAccordions()
    this.bindInputs()
    this.bindActions()
    this.updateCount()
  }

  collectItems() {
    return Array.from(this.grid.querySelectorAll('[data-product-item]')).map((el) => {
      const groups = {}
      for (const { name, value } of el.attributes) {
        if (name.startsWith('data-filter-')) {
          groups[name.slice('data-filter-'.length)] = value.split(',').filter(Boolean)
        }
      }
      return {
        el,
        price: parseFloat(el.dataset.price) || 0,
        order: parseFloat(el.dataset.order) || 0,
        groups,
      }
    })
  }

  bindTriggers() {
    document.querySelectorAll('[data-filter-toggle]').forEach((btn) => {
      btn.addEventListener('click', () => this.open(btn))
    })
  }

  bindAccordions() {
    this.querySelectorAll('[data-filter-accordion-trigger]').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const open = trigger.getAttribute('aria-expanded') !== 'true'
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
      })
    })
  }

  bindInputs() {
    this.querySelectorAll('[data-filter-input], [data-filter-sort]').forEach((input) => {
      input.addEventListener('change', () => this.updateCount())
    })
  }

  bindActions() {
    this.applyBtn?.addEventListener('click', () => {
      this.apply()
      this.close()
    })

    document.querySelectorAll('[data-filter-clear]').forEach((btn) => {
      btn.addEventListener('click', () => this.clearAll())
    })
  }

  getSelectedFilters() {
    const filters = {}
    this.querySelectorAll('[data-filter-input]:checked').forEach((input) => {
      const group = input.getAttribute('data-filter-group')
      if (!group) return
      if (!filters[group]) filters[group] = []
      filters[group].push(input.value.trim().toLowerCase())
    })
    return filters
  }

  getSelectedSort() {
    const checked = this.querySelector('[data-filter-sort]:checked')
    return checked ? checked.value : null
  }

  itemMatches(item, filters) {
    return Object.keys(filters).every((group) => {
      const selected = filters[group]
      if (!selected.length) return true
      const values = item.groups[group] || []
      return selected.some((value) => values.includes(value))
    })
  }

  countMatches() {
    const filters = this.getSelectedFilters()
    return this.items.filter((item) => this.itemMatches(item, filters)).length
  }

  updateCount() {
    if (!this.countLabels.length) return
    const count = this.countMatches()
    this.countLabels.forEach((el) => (el.textContent = `${this.showProductsText} (${count})`))
  }

  apply() {
    const filters = this.getSelectedFilters()
    let visibleCount = 0

    this.items.forEach((item) => {
      const matches = this.itemMatches(item, filters)
      item.el.classList.toggle('hidden', !matches)
      if (matches) visibleCount += 1
    })

    this.sortItems(this.getSelectedSort())
    this.toggleEmptyState(visibleCount === 0)
  }

  sortItems(sort) {
    if (!this.grid) return

    const ordered = this.items.slice()
    if (sort === 'price-ascending' || sort === 'price-descending') {
      ordered.sort((a, b) => (sort === 'price-ascending' ? a.price - b.price : b.price - a.price))
    } else {
      ordered.sort((a, b) => a.order - b.order)
    }

    const fragment = document.createDocumentFragment()
    ordered.forEach((item) => fragment.appendChild(item.el))
    this.grid.appendChild(fragment)
  }

  toggleEmptyState(isEmpty) {
    if (this.grid) this.grid.classList.toggle('hidden', isEmpty)
    if (!this.emptyEl) return
    this.emptyEl.classList.toggle('hidden', !isEmpty)
    this.emptyEl.classList.toggle('flex', isEmpty)
  }

  clearAll() {
    this.querySelectorAll('[data-filter-input], [data-filter-sort]').forEach((input) => {
      input.checked = false
    })
    this.apply()
    this.updateCount()
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy

    requestAnimationFrame(() => {
      this.classList.remove('opacity-0', 'invisible')
      this.classList.add('opacity-100', 'visible')
      this.panel?.classList.remove('translate-x-full')
      this.panel?.classList.add('translate-x-0')
    })

    this.addEventListener(
      'transitionend',
      () => {
        if (typeof trapFocus === 'function') {
          const closeButton = this.querySelector('#FilterDrawer-Panel button')
          trapFocus(this, closeButton || this.panel)
        }
      },
      { once: true },
    )

    document.body.classList.add('overflow-hidden')
  }

  close() {
    this.classList.remove('opacity-100', 'visible')
    this.classList.add('opacity-0', 'invisible')
    this.panel?.classList.remove('translate-x-0')
    this.panel?.classList.add('translate-x-full')

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')
  }
}

customElements.define('filter-drawer', FilterDrawer)
